import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import Loading from "@/components/Loading"; // Seu componente Loading

/* ==========================================================================
 * Carregar variáveis do env-config.js
 * ========================================================================== */
const ENV = (window as any).ENV || {};
const { VITE_API_URL, VITE_NUVIDIO, VITE_VIDAS_URL } = ENV;

// Apenas para garantir que as variáveis existam antes de usar.
// O `throw new Error` garante que a aplicação não continue com configurações inválidas.
if (!VITE_API_URL || !VITE_NUVIDIO || !VITE_VIDAS_URL) {
    const missingVars = [];
    if (!VITE_API_URL) missingVars.push("VITE_API_URL");
    if (!VITE_NUVIDIO) missingVars.push("VITE_NUVIDIO");
    if (!VITE_VIDAS_URL) missingVars.push("VITE_VIDAS_URL");
    const errorMessage = `[ENV] Variáveis do env-config.js não carregadas corretamente: ${missingVars.join(", ")}.`;
    console.error(errorMessage, ENV);
    throw new Error(errorMessage);
}

/* ---------------- Styled ---------------- */
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh; /* Ocupa a altura total da viewport */
  width: 100%;
  padding: 1rem;
  background-color: var(--gray-50); /* Usando Design Token */
  overflow: hidden;
  text-align: center;
`;

const ErrorMessage = styled.h2`
  font-size: var(--fs-h3); /* Usando Design Token */
  color: var(--error-500); /* Usando Design Token */
  font-weight: 600;
  background-color: #ffeaea; /* Cor específica, pode ser um token se existir */
  border: 1px solid #ffcccc; /* Cor específica, pode ser um token se existir */
  padding: 1rem 2rem;
  border-radius: var(--radius); /* Usando Design Token */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  max-width: 480px;
  width: 100%;
  line-height: 1.4;
`;

/* ---------------- Component ---------------- */
export default function Home() {
    const [search] = useSearchParams();
    const navigate = useNavigate();

    const [message, setMessage] = useState("Inicializando..."); // Mensagem para o Loading component
    const [statusError, setStatusError] = useState<string | null>(null); // Erro para ser exibido na tela
    const [attempts, setAttempts] = useState(0);

    const MAX_ATTEMPTS = 3;
    const WS_URL = `wss://127.0.0.1:22501/services`; // Constante para a URL do WebSocket

    const code = search.get("code") || "";
    const opaque = search.get("opaque") || "";

    // Ref para garantir que o processo de captura não seja iniciado múltiplas vezes
    const isCapturingRef = useRef(false);

    // Ref para o objeto WebSocket, para poder fechá-lo na limpeza
    const socketRef = useRef<WebSocket | null>(null);

    const redirectToLogin = useCallback(() => {
        const params = new URLSearchParams();
        if (code) params.append("code", code);
        if (opaque) params.append("opaque", opaque);
        const targetUrl = `/login${params.toString() ? "?" + params.toString() : ""}`;
        navigate(targetUrl, { replace: true });
    }, [navigate, code, opaque]);

    const startCaptureProcess = useCallback(() => {
        if (isCapturingRef.current) {
            console.log("[Home] Processo de captura já em andamento.");
            return;
        }
        isCapturingRef.current = true; // Sinaliza que o processo começou

        const dnaStored = sessionStorage.getItem("dna");
        if (dnaStored) {
            console.log("[DNA] DNA já presente no sessionStorage. Redirecionando para login.");
            setMessage("DNA encontrado. Redirecionando...");
            redirectToLogin();
            isCapturingRef.current = false; // Resetar para caso haja algum fluxo inesperado
            return;
        }

        if (attempts >= MAX_ATTEMPTS) {
            const errorMessage = "VAgent não encontrado ou DNA não capturado após várias tentativas.";
            console.error(`[VAgent] ${errorMessage}`);
            setStatusError(errorMessage);
            isCapturingRef.current = false; // Resetar
            return;
        }

        setMessage(`Tentando conectar ao VAgent... tentativa ${attempts + 1} de ${MAX_ATTEMPTS}`);
        console.log(`[VAgent] Tentando conexão: ${attempts + 1}`);

        let socket: WebSocket;
        try {
            socket = new WebSocket(WS_URL);
            socketRef.current = socket; // Armazena a referência para o socket

            socket.onopen = () => {
                console.log("[VAgent] Conexão aberta.");
                setMessage("Conectado ao VAgent. Solicitando DNA...");
                socket.send(
                    JSON.stringify({
                        plugin: "listener-vsentinel",
                        action: "Plugin.RetrieveDNA",
                        content: JSON.stringify({ useProxyWMI: false }),
                    })
                );
            };

            socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.action === "Plugin.RetrieveDNA" && data.content?.dna) {
                        const dna = data.content.dna;
                        console.log("[DNA] DNA capturado com sucesso!");
                        sessionStorage.setItem("dna", dna);
                        setMessage("DNA capturado. Redirecionando...");
                        redirectToLogin();
                    } else {
                        console.warn("[DNA] Resposta do VAgent não contém DNA válido.");
                        setStatusError("VAgent não retornou DNA válido. Verifique a instalação.");
                    }
                } catch (err) {
                    console.error("[DNA] Erro ao processar mensagem do WebSocket:", err);
                    setStatusError("Erro ao processar resposta do agente DNA.");
                } finally {
                    // Fechar o socket após processar a mensagem (sucesso ou falha na resposta)
                    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                        socketRef.current.close();
                    }
                }
            };

            socket.onerror = (err) => {
                console.error("[VAgent] Erro na conexão WebSocket:", err);
                setStatusError("Falha na conexão com o VAgent. Verifique se está em execução.");
                if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                    socketRef.current.close();
                }
            };

            socket.onclose = () => {
                console.log("[VAgent] Conexão fechada.");
                isCapturingRef.current = false; // Permite uma nova tentativa
                if (!dnaStored && !statusError) { // Só incrementa tentativas se não capturou DNA e não há erro final
                    setAttempts((prev) => prev + 1);
                }
            };

        } catch (err) {
            console.error("[VAgent] Erro fatal ao iniciar WebSocket:", err);
            setStatusError("Erro crítico ao tentar conectar com VAgent. Recarregue a página.");
            isCapturingRef.current = false; // Resetar
            if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                socketRef.current.close();
            }
        }
    }, [attempts, redirectToLogin, statusError]); // Adicionado statusError para evitar loop quando ele é setado

    useEffect(() => {
        // Inicia o processo de captura.
        // O useCallback de startCaptureProcess garante que ele só mude se suas dependências mudarem.
        // hasStartedRef.current já lida com a execução única na primeira renderização/montagem.
        if (!statusError) { // Só tenta iniciar se não houver um erro final
            startCaptureProcess();
        }

        // Cleanup: Fecha o WebSocket se o componente for desmontado
        return () => {
            if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                socketRef.current.close();
            }
        };
    }, [startCaptureProcess, statusError]); // startCaptureProcess é uma dep, statusError para reavaliar tentativas

    // useEffect para re-tentar se o erro for temporário e ainda tiver tentativas
    useEffect(() => {
        if (statusError && attempts < MAX_ATTEMPTS && !isCapturingRef.current) {
            // Limpa o erro para tentar novamente (se for um erro temporário, não um erro crítico de init)
            if (statusError.includes("VAgent não retornou DNA válido") ||
                statusError.includes("Falha na conexão com o VAgent")) {
                console.log("[VAgent] Tentando novamente após erro temporário...");
                const timer = setTimeout(() => {
                    setStatusError(null); // Limpa o erro para permitir nova tentativa
                    startCaptureProcess();
                }, 2000); // Espera 2 segundos antes de tentar novamente
                return () => clearTimeout(timer);
            }
        }
    }, [statusError, attempts, startCaptureProcess]);


    // Renderização condicional
    if (statusError) {
        return (
            <Wrapper>
                <ErrorMessage>{statusError}</ErrorMessage>
            </Wrapper>
        );
    }

    return (
        <Wrapper> {/* Coloca o Loading dentro do Wrapper para centralizá-lo corretamente */}
            <Loading message={message} />
        </Wrapper>
    );
}